/**
 * API Service Layer for DealFlow AI
 * Handles all communication with the backend API
 */

import axios, { AxiosInstance } from 'axios';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://dealflow.bill-burkey.workers.dev/api/v1';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor for auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('authToken');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Properties API
  async searchProperties(params: PropertySearchParams): Promise<Property[]> {
    const response = await this.api.get('/properties/search', { params });
    return response.data.properties;
  }

  async getPropertyById(id: string): Promise<Property> {
    const response = await this.api.get(`/properties/${id}`);
    return response.data.property;
  }

  async triggerPropertyScan(userId: string, params: ScanParams): Promise<ScanResult> {
    const response = await this.api.post('/properties/scan', { userId, ...params });
    return response.data;
  }

  async getScanProgress(scanId: string): Promise<ScanProgress> {
    const response = await this.api.get(`/properties/scan/${scanId}/progress`);
    return response.data;
  }

  // Analysis API
  async analyzeProperty(propertyId: string): Promise<PropertyAnalysis> {
    const response = await this.api.post(`/analysis/property/${propertyId}`);
    return response.data.analysis;
  }

  async getAnalysisHistory(userId: string): Promise<AnalysisHistoryItem[]> {
    const response = await this.api.get(`/analysis/history?userId=${userId}`);
    return response.data.analyses;
  }

  async generateReport(analysisId: string, format: 'pdf' | 'html' = 'pdf'): Promise<string> {
    const response = await this.api.post(`/analysis/${analysisId}/report`, { format });
    return response.data.reportUrl;
  }

  // User API
  async registerUser(userData: UserRegistrationData): Promise<AuthResponse> {
    const response = await this.api.post('/users/register', userData);
    return response.data;
  }

  async loginUser(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await this.api.post('/users/login', credentials);
    return response.data;
  }

  async getUserProfile(userId: string): Promise<UserProfile> {
    const response = await this.api.get(`/users/${userId}/profile`);
    return response.data.user;
  }

  async updateUserProfile(userId: string, updates: Partial<UserProfile>): Promise<UserProfile> {
    const response = await this.api.put(`/users/${userId}/profile`, updates);
    return response.data.user;
  }

  // Dashboard API
  async getDashboardData(userId: string): Promise<DashboardData> {
    const response = await this.api.get(`/dashboard/${userId}`);
    return response.data;
  }

  async getMarketTrends(location: string): Promise<MarketTrends> {
    const response = await this.api.get(`/analysis/market-trends?location=${location}`);
    return response.data;
  }
}

// Export singleton instance
export const apiService = new ApiService();

// Type definitions
export interface PropertySearchParams {
  location?: string;
  minPrice?: number;
  maxPrice?: number;
  propertyType?: string;
  bedrooms?: number;
  bathrooms?: number;
  page?: number;
  limit?: number;
}

export interface Property {
  id: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  price: number;
  bedrooms: number;
  bathrooms: number;
  sqft: number;
  propertyType: string;
  yearBuilt: number;
  lotSize?: number;
  images: string[];
  description?: string;
  coordinates: {
    lat: number;
    lng: number;
  };
  createdAt: string;
  updatedAt: string;
}

export interface ScanParams {
  location: string;
  maxPrice: number;
  minPrice: number;
  propertyTypes: string[];
}

export interface ScanResult {
  scanId: string;
  status: 'started' | 'processing' | 'completed' | 'failed';
  message: string;
  estimatedCompletion?: string;
}

export interface ScanProgress {
  scanId: string;
  status: string;
  progress: number;
  propertiesScanned: number;
  propertiesFound: number;
  estimatedCompletion?: string;
}

export interface PropertyAnalysis {
  id: string;
  propertyId: string;
  userId: string;
  score: number;
  cashFlow: number;
  capRate: number;
  roiProjection: number;
  marketValue: number;
  rentEstimate: number;
  expenses: {
    taxes: number;
    insurance: number;
    maintenance: number;
    management: number;
    vacancy: number;
  };
  aiInsights: string[];
  risks: string[];
  opportunities: string[];
  createdAt: string;
}

export interface UserRegistrationData {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  investment_experience?: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface AuthResponse {
  success: boolean;
  token: string;
  user: UserProfile;
}

export interface UserProfile {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  investment_experience?: string;
  subscription_tier: string;
  api_usage_current?: number;
  api_usage_limit?: number;
}

export interface AnalysisHistoryItem {
  id: string;
  propertyId: string;
  propertyAddress: string;
  score: number;
  analyzedAt: string;
}

export interface DashboardData {
  userId: string;
  stats: {
    totalAnalyses: number;
    avgScore: number;
    portfolioValue: number;
    monthlyRentEstimate: number;
  };
  recentAnalyses: AnalysisHistoryItem[];
  marketTrends: MarketTrends;
  watchlist: Property[];
}

export interface MarketTrends {
  location: string;
  medianPrice: number;
  priceChange: number;
  medianRent: number;
  rentChange: number;
  daysOnMarket: number;
  inventoryLevel: number;
  trends: Array<{
    date: string;
    price: number;
    rent: number;
  }>;
}
